// src/pages/public/Services/ServicesPage.jsx
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FaArrowRight } from 'react-icons/fa6'

import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { fetchPageBySlug } from '../../../redux/slices/cmsSlice.js'
import { fetchServices } from '../../../redux/slices/servicesSlice.js'

import SEO from '../../../components/seo/SEO.jsx'
import { LazyImage } from '../../../components/common/LazyImage.jsx'
import EmptyState from '../../../components/common/EmptyState.jsx'
import Cards from '../../../components/common/Cards.jsx'

import './ServicesPage.scss'
import BuildSolution from '../../../components/common/Common/BuildSolution/BuildSolution.jsx'

export default function ServicesPage() {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { currentPage: servicesPage, loading: pageLoading } = useAppSelector(
        (state) => state.cms
    )
    const { services, loading: servicesLoading } = useAppSelector(
        (state) => state.services
    )

    useEffect(() => {
        dispatch(fetchPageBySlug('services'))
        dispatch(fetchServices())
    }, [dispatch])

    const getSectionData = (sectionKey) => {
        return servicesPage?.sections?.find((section) => section.key === sectionKey)
            ?.data
    }

    const heroSection = getSectionData('hero')
    const cardSection = getSectionData('cardsSection')
    const servicesListSection = getSectionData('servicesList')

    if (pageLoading && !servicesPage) {
        return (
            <>
                <SEO title="Our Services" />
                <div className="services-page-root">
                    <section className="minimal-hero-section-services">
                        <div className="minimal-hero-section skeleton-hero" />
                    </section>
                </div>
            </>
        )
    }

    return (
        <>
            <SEO
                title={servicesPage?.seoTitle || 'Our Services'}
                description={servicesPage?.seoDescription}
            />

            <div className="services-page-root">

                <section className="minimal-hero-section-services">
                    <div className="minimal-hero-section">
                        <div className="minimal-hero-container">
                            {/* Center Image */}
                            {heroSection?.imageUrl && (
                                <motion.div
                                    className="center-image-container"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <LazyImage
                                        src={heroSection?.imageUrl}
                                        alt={heroSection?.hero?.heading || 'Services Icon'}
                                        className="center-image"
                                        placeholder={<div className="center-image skeleton-bg" />}
                                    />
                                </motion.div>
                            )}

                            {/* Heading */}
                            <motion.h1
                                className="minimal-heading"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <span className="heading-line">
                                    {(heroSection?.heading || 'Our Services').toUpperCase()}
                                </span>
                            </motion.h1>

                            {/* Subheading */}
                            <motion.p
                                className="minimal-subheading"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                            >
                                {heroSection?.subheading ||
                                    'Comprehensive digital solutions tailored to your business needs.'}
                            </motion.p>
                        </div>
                    </div>
                </section>

                {/* =========================
            SERVICES LIST – v1 Industries-style cards
           ========================= */}
                <section className="services-listing">
                    <div className="services-listing-inner">
                        <motion.h2
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            {(
                                servicesListSection?.heading ||
                                'Industries we serve'
                            ).toUpperCase()}
                        </motion.h2>

                        {servicesLoading ? (
                            <div className="industry-cards">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="industry-card skeleton-card">
                                        <div className="image-container skeleton-bg" />
                                        <div className="content">
                                            <div className="skeleton-line skeleton-line-lg" />
                                            <div className="skeleton-line" />
                                            <div className="skeleton-line short" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : services.length > 0 ? (
                            <div className="industry-cards">
                                {services.map((service, index) => {
                                    const imageSrc =
                                        service.cardImageUrl ||
                                        service.heroImageUrl ||
                                        service.imageUrl

                                    const features = service.cardSpans || []
                                    const bullets =
                                        service.bulletPoints ||
                                        service.benefits ||
                                        service.tags ||
                                        []

                                    return (
                                        <motion.div
                                            key={service._id}
                                            className="industry-card"
                                            initial={{ opacity: 0, y: 50 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.6, delay: index * 0.1 }}
                                            viewport={{ once: true }}
                                            whileHover={{ y: -10, transition: { duration: 0.2 } }}
                                        >
                                            {/* Left image */}
                                            <div className="image-container">
                                                {imageSrc ? (
                                                    <LazyImage
                                                        src={imageSrc}
                                                        alt={service.name}
                                                        className="service-main-image"
                                                        placeholder={
                                                            <div className="service-main-image skeleton-bg" />
                                                        }
                                                    />
                                                ) : (
                                                    <div className="service-main-image skeleton-bg" />
                                                )}
                                            </div>

                                            {/* Right content */}
                                            <div className="content">
                                                <h3>{service.name}</h3>

                                                {service.shortDescription && (
                                                    <p>{service.shortDescription}</p>
                                                )}

                                                {/* Features grid (from cardSpans or similar) */}
                                                {features.length > 0 && (
                                                    <div className="features-grid">
                                                        {features.map((text, i) => (
                                                            <motion.div
                                                                key={i}
                                                                className="feature-item"
                                                                initial="hidden"
                                                                whileInView="visible"
                                                                viewport={{ once: true }}
                                                                variants={{
                                                                    hidden: { opacity: 0, y: 20 },
                                                                    visible: {
                                                                        opacity: 1,
                                                                        y: 0,
                                                                        transition: { duration: 0.5 },
                                                                    },
                                                                }}
                                                            >
                                                                <span className="feature-icon">
                                                                    <div className="feature-icon-placeholder">
                                                                        {i + 1}
                                                                    </div>
                                                                </span>
                                                                <p>{text}</p>
                                                            </motion.div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Bullet list – optional if present */}
                                                {Array.isArray(bullets) && bullets.length > 0 && (
                                                    <ul>
                                                        {bullets.map((item, i) => (
                                                            <li key={i}>{item}</li>
                                                        ))}
                                                    </ul>
                                                )}

                                                {/* Learn more button */}
                                                <motion.button
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => navigate(`/services/${service.slug}`)}
                                                >
                                                    <span
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '1rem',
                                                        }}
                                                    >
                                                        Learn More <FaArrowRight />
                                                    </span>
                                                </motion.button>
                                            </div>
                                        </motion.div>
                                    )
                                })}
                            </div>
                        ) : (
                            <EmptyState
                                title="No Services Available"
                                message="We're currently updating our service offerings. Please check back soon."
                            />
                        )}
                    </div>
                </section>

                <BuildSolution />

                {/* =========================
            EXTRA CARDS SECTION (v2 Cards, styled to match)
           ========================= */}
                <section className="services-extra-section">
                    <div className="services-extra-container">
                        {cardSection?.capsuleText && (
                            <span className="centered-capsule">
                                {cardSection.capsuleText}
                            </span>
                        )}
                        {cardSection?.heading && (
                            <h2 className="extra-heading">{cardSection.heading?.toUpperCase()}</h2>
                        )}
                        <Cards />
                    </div>
                </section>
            </div>
        </>
    )
}
